import { AgentSessionStatusSchema, type AgentRole } from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@paguz/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { BotIcon, ClipboardListIcon, PowerIcon, RadioIcon, ShieldAlertIcon } from 'lucide-react';
import { useState } from 'react';
import type { AgentSessionsSearch } from '../../app/search.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog } from '../../components/confirm-dialog.tsx';
import { Checkbox, Field, Select, compactInputClass } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime, formatInt } from '../../lib/format.ts';
import { useCan } from '../../lib/session.ts';
import { AGENT_LABEL, CONVERSATIONAL_AGENTS, SESSION_STATUS } from './labels.ts';
import { UsagePanel } from './usage-panel.tsx';
import { inputBaseClass } from '@paguz/ui';

const route = getRouteApi('/app/agentes');

/** Sesiones en vivo e históricas, presupuesto del día y kill switch global (`06-backoffice.md` §4.7). */
export function AgentsPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const canTasks = useCan('hitl:read');
  const query = useQuery({
    queryKey: queryKeys.agentSessions(search),
    queryFn: () => api.listAgentSessions(search),
    placeholderData: keepPreviousData,
    // Sesiones en vivo: sondeo cada 5 s (la API expondrá `/live` por SSE).
    refetchInterval: 5_000,
  });
  const setFilters = (next: AgentSessionsSearch) => {
    void navigate({ search: { ...next, cursor: undefined } });
  };
  return (
    <>
      <PageHeader
        title="Agentes"
        description="Conversaciones del Asesor IA y del Agente de Compras con su traza completa: llamadas al modelo, herramientas, guardas y costo. Los agentes preparan; nunca mueven dinero ni aprueban créditos."
        actions={
          canTasks && (
            <Button variant="outline" asChild>
              <Link to="/tareas">
                <ClipboardListIcon /> Tareas HITL
              </Link>
            </Button>
          )
        }
      />
      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-5">
          <Panel bodyClassName="flex flex-wrap items-end gap-4">
            <Field label="Agente" htmlFor="ag-agent" className="w-56">
              <Select
                id="ag-agent"
                value={search.agent ?? ''}
                onChange={(e) => {
                  setFilters({
                    ...search,
                    agent: (e.target.value || undefined) as AgentRole | undefined,
                  });
                }}
              >
                <option value="">Todos</option>
                {CONVERSATIONAL_AGENTS.map((a) => (
                  <option key={a} value={a}>
                    {AGENT_LABEL[a]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Estado" htmlFor="ag-status" className="w-48">
              <Select
                id="ag-status"
                value={search.status ?? ''}
                onChange={(e) => {
                  setFilters({
                    ...search,
                    status: (e.target.value || undefined) as AgentSessionsSearch['status'],
                  });
                }}
              >
                <option value="">Todos</option>
                {AgentSessionStatusSchema.options.map((s) => (
                  <option key={s} value={s}>
                    {SESSION_STATUS[s].label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Cliente o ID" htmlFor="ag-q" className="w-56">
              <input
                id="ag-q"
                defaultValue={search.q ?? ''}
                className={cn(inputBaseClass, compactInputClass)}
                placeholder="Nombre, ses_…, app_…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    setFilters({ ...search, q: e.currentTarget.value || undefined });
                }}
              />
            </Field>
            <Checkbox
              label="En vivo"
              checked={search.live === 'true'}
              onCheckedChange={(on) => {
                setFilters({ ...search, live: on ? 'true' : undefined });
              }}
            />
            <Checkbox
              label="Con guardas disparadas"
              checked={search.withGuards === 'true'}
              onCheckedChange={(on) => {
                setFilters({ ...search, withGuards: on ? 'true' : undefined });
              }}
            />
          </Panel>
          <Panel bodyClassName="p-0">
            {query.isPending ? (
              <div className="p-5">
                <LoadingRows />
              </div>
            ) : query.isError ? (
              <div className="p-5">
                <ErrorState error={query.error} onRetry={() => void query.refetch()} />
              </div>
            ) : query.data.data.length === 0 ? (
              <div className="p-5">
                <EmptyState icon={<BotIcon />} title="No hay sesiones con esos filtros" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-center">Turnos</TableHead>
                    <TableHead className="text-center">Guardas</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead>Última actividad</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {query.data.data.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Link
                          to="/agentes/sesiones/$sessionId"
                          params={{ sessionId: s.id }}
                          className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
                        >
                          {s.live && (
                            <RadioIcon className="size-3.5 text-success" aria-label="En vivo" />
                          )}
                          {s.customer?.fullName ?? 'Sin cliente'}
                        </Link>
                        <div className="max-w-56 truncate text-xs text-muted-foreground">
                          {s.preview}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {AGENT_LABEL[s.agent]}
                        <div className="text-xs text-muted-foreground">{s.channel}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={SESSION_STATUS[s.status].tone}>
                          {SESSION_STATUS[s.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center tabular-nums">{s.turns}</TableCell>
                      <TableCell className="text-center">
                        {s.guardsTriggered > 0 ? (
                          <span className="inline-flex items-center gap-1 font-semibold text-warning-soft-foreground">
                            <ShieldAlertIcon className="size-4" /> {s.guardsTriggered}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatInt(
                          s.tokens.input +
                            s.tokens.output +
                            s.tokens.cacheRead +
                            s.tokens.cacheWrite,
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        USD {s.costUsd}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                        {formatDateTime(s.lastActivityAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>
        </div>
        <div className="order-first grid gap-6 lg:grid-cols-2 2xl:order-none 2xl:grid-cols-1 2xl:self-start">
          <UsagePanel compact />
          <KillSwitchPanel />
        </div>
      </div>
    </>
  );
}

function KillSwitchPanel() {
  const canSwitch = useCan('agents:kill-switch');
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: queryKeys.setting('ai'),
    queryFn: () => api.getSetting('ai'),
  });
  const [target, setTarget] = useState<{ agent: AgentRole; enabled: boolean } | null>(null);
  const mutation = useMutation({
    mutationFn: (input: { agent: AgentRole; enabled: boolean; reason: string }) =>
      api.setKillSwitch(input.agent, input.enabled, input.reason),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.setting('ai') });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
      toast.success(
        `${AGENT_LABEL[input.agent]}: ${input.enabled ? 'reactivado' : 'detenido (kill switch)'}`,
      );
    },
  });
  const enabled = settings.data?.value.enabled;
  return (
    <Panel
      title="Kill switch global"
      description="Detiene un agente para todos los clientes: el canal muestra un mensaje de mantenimiento y ofrece contacto humano."
    >
      {!enabled ? (
        <LoadingRows rows={3} />
      ) : (
        <ul className="flex flex-col gap-3">
          {CONVERSATIONAL_AGENTS.map((agent) => {
            const on = enabled[agent];
            return (
              <li key={agent} className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{AGENT_LABEL[agent]}</p>
                  <Badge variant={on ? 'success' : 'danger'}>{on ? 'Operando' : 'Detenido'}</Badge>
                </div>
                {canSwitch && (
                  <Button
                    size="sm"
                    variant={on ? 'destructive' : 'outline'}
                    onClick={() => {
                      setTarget({ agent, enabled: !on });
                    }}
                  >
                    <PowerIcon /> {on ? 'Detener' : 'Reactivar'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!canSwitch && (
        <p className="mt-3 text-xs text-muted-foreground">
          Solo un administrador activa el kill switch global.
        </p>
      )}
      <ConfirmActionDialog
        open={target !== null}
        onOpenChange={(o) => {
          if (!o) setTarget(null);
        }}
        title={
          target ? `${target.enabled ? 'Reactivar' : 'Detener'} ${AGENT_LABEL[target.agent]}` : ''
        }
        description={
          target?.enabled
            ? 'El agente vuelve a responder en todos los canales.'
            : 'Las sesiones abiertas dejan de recibir respuestas del agente hasta que lo reactives.'
        }
        destructive={target ? !target.enabled : false}
        confirmLabel={target?.enabled ? 'Reactivar agente' : 'Detener agente'}
        onConfirm={async (reason) => {
          if (target) await mutation.mutateAsync({ ...target, reason });
        }}
      />
    </Panel>
  );
}
